import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DataAction, Payload, StateRepository } from '@angular-ru/ngxs/decorators';
import { NgxsDataRepository } from '@angular-ru/ngxs/repositories';
import { Selector, State } from '@ngxs/store';
import { produce } from 'immer';
import { BehaviorSubject, Observable, map } from 'rxjs';
import { IFacilityNote } from 'src/app/models/facility';
import {
    IFacilityListFilter,
    IFacilityRequestConfig,
    IFacilityRequestForSmartAction,
    IFacilityRequestListItem,
    IFacilityRequestListItemWrapper,
    IFacilityRequestSearchResponse,
} from 'src/app/models/facility-request';
import { MemberSearchService } from 'src/app/services/member/member-search.service';
import { Utilities } from 'src/app/services/utilities';
import { GridFilterItemFinal, GridFilterProperty, GridFilterPropertyOperator, IFacetQueryItem, PagingQuery } from 'src/app/shared/grid/grid.model';

import { AlertService } from '../alert.service';
import { FacilityEndpoint } from '../facility-endpoint.service';
import { LabelValueLower } from './../../models/keyvalue';
import { FacilityRequestStateModel } from './facility-request.statemodels';
import { FacilityRequestSmartActionsService } from './smart-actions.service';
import { AppInsightsService } from '../app-insights/appinsights.service';
import { IIndexSearchRequest } from 'src/app/models/index-search';
import { SelectItem } from 'primeng/api';


export class BuildSmartAction {
    static readonly type = `[FacilityRequest] BuildSmart`;
}


@StateRepository()
@State<FacilityRequestStateModel>({
    name: 'facilityRequestState',
    defaults: {
        currentFacilityRequestListItem: null,
        smartActions: null,
        currentFacilityRequestNotes: [],
        currentFacilityRequest: null
    }
})
@Injectable()
export class FacilityRequestService extends NgxsDataRepository<FacilityRequestStateModel> {


    loadCustomView$ = new BehaviorSubject<string>(null);




    @Selector()
    static currentListItem(state: FacilityRequestStateModel) {
        return state.currentFacilityRequestListItem;
    }

    @Selector()
    static currentList(state: FacilityRequestStateModel) {
        return state.currentFacilityRequest;
    }

    @Selector()
    static facilityRequestSmartActions(state: FacilityRequestStateModel) {
        return state.smartActions;
    }

    @Selector()
    static currentFacilityRequestTab(state: FacilityRequestStateModel) {
        return state.currentFacilityRequestTab;
    }

    @Selector()
    static notes(state: FacilityRequestStateModel) {
        return state.currentFacilityRequestNotes;
    }

    constructor(
        private endpoint: FacilityEndpoint,
        public router: Router,
        public route: ActivatedRoute,
        public smartActionService: FacilityRequestSmartActionsService,
        public searchService: MemberSearchService,
        public alertService: AlertService,
        public appInsightService: AppInsightsService
    ) {
        super();
    }


    ngxsAfterBootstrap() {
        super.ngxsAfterBootstrap();
    }


    getPage(pagingQuery: PagingQuery, filter: IFacilityListFilter): Observable<IFacilityRequestSearchResponse> {
        
        let columnFilters = filter?.columnFilters?.filter(item => item.valid && item.property && item.operator && item.value)
            .map(columnFilter => new GridFilterItemFinal(columnFilter));
                
        columnFilters = columnFilters && columnFilters.filter(item => item.value);

        const facets: IFacetQueryItem[] = null;

        const body: IIndexSearchRequest = {
            first: pagingQuery.$first,
            rows: pagingQuery.$rows,
            sort: pagingQuery.$sort,
            criteria: filter.criteria,
            tenants: filter.tenants,
            columnFilters: columnFilters,
            facets: facets || []
        };

        const result = 
            this
                .endpoint
                .fuzzySearch(body)
                // Filter out results with a `null` `Facility` property
                .pipe(
                    map((res: IFacilityRequestSearchResponse) => {
                        
                        res.value.forEach((element: IFacilityRequestListItemWrapper, index: number, array :IFacilityRequestListItemWrapper[]) => {

                            if (!element.document.Facility) {
                                this.appInsightService.logEvent('INVALID FACILITY REQUEST RECORD', {
                                    tenantName: element.document.TenantName,
                                    facilityRequestExternalId: element.document.FacilityRequestExternalId
                                });
                                
                                array.splice(index, 1);
                            }
                        });

                        return res;
                    })
                );

        return result;
    }

    getFilters() : Observable<GridFilterProperty[]> {
        return this.endpoint.getMetaData().pipe(map(metadata => {
           
           const gridFilterProperties: GridFilterProperty[] = [];
           
           metadata.filters.forEach(filter => {
               const operators = (filter.isFacetable ? ['equal to'] : ['equal to', 'not equal to', 'starts with']) as unknown as GridFilterPropertyOperator[];
               const displayComponent = (filter.isFacetable ? 'multiselect' : 'input');
               const filterItem = new GridFilterProperty(filter.property, filter.property, operators, displayComponent);

               filterItem.setValueFunc((() => {
                   if (filter.values) {                            
                       return (filter.values || []).map(val => ({
                           label: val.name,
                           value: val.name
                       }));
                   }
               }));

               gridFilterProperties.push(filterItem);
           });

           return gridFilterProperties;
       }));
   }

    getCompleteOptions(): Promise<LabelValueLower[]> {
        return Promise.resolve([
            {
                value: 'MANUAL_UPLOAD',
                label: 'Chart Uploaded Manually',
            },
            {
                value: 'NO_CHART_FOUND',
                label: 'No chart Found',
            },
            {
                value: 'INVALID_AUTH',
                label: 'Invalid Authorization',
            },
            {
                value: 'NO_UTIL',
                label: 'No utilization',
            },
            {
                value: 'REJECTED_BY_FACILITY',
                label: 'Rejected by facility ',
            },
            {
                value: 'REQUEST_CANCELLED',
                label: 'Request canceled ',
            }


        ] as LabelValueLower[]);
    }

    @DataAction()
    async advanceStage(@Payload('base64') base64: string, @Payload('Facility Request External Id') facilityRequestExternalId: string, @Payload('action')action : string, @Payload('reson') reason: any) {
        const responseCompletion = await this.endpoint.advanceStage(base64, facilityRequestExternalId, action, reason).toPromise();

        const parts = Utilities.splitInTwo(atob(base64), '|');
        const tenantId = parts.firstPart;
        const memberId = Utilities.splitInTwo(parts.secondPart, '|').firstPart;

        let current: IFacilityRequestListItem = null;

        const response = await this.endpoint.getFacilityRequest(memberId, tenantId, facilityRequestExternalId).toPromise();

        if (response) {
             // MAJOR HACK
             response.Facility = response.facilityMetadata.Facility;

            this.ctx.setState(produce(this.ctx.getState(), (draft: FacilityRequestStateModel) => {
                    draft.currentFacilityRequest = response;
            }));

            return response;
        }

        return null;
    }



    @DataAction()
    async getFacilityRequest(@Payload('config') payload: IFacilityRequestConfig) {

        if (payload) {
            const response = await this.endpoint.getFacilityRequest(payload.memberId, payload.tenantId, payload.facilityRequestExternalId).toPromise();

            if (response) {

                // MAJOR HACK
                response.Facility = response.facilityMetadata.Facility;

                this.ctx.setState(produce(this.ctx.getState(), (draft: FacilityRequestStateModel) => {
                    draft.currentFacilityRequest = response;
                }));

                return response;
            }
        }

        return null;
    }

    @DataAction()
    async clearFacilityRequest() {
        this.ctx.setState(produce(this.ctx.getState(), (draft: FacilityRequestStateModel) => {
            draft.currentFacilityRequestListItem = this.initialState.currentFacilityRequestListItem;
            draft.currentFacilityRequest = this.initialState.currentFacilityRequest;
            draft.currentFacilityRequestNotes = this.initialState.currentFacilityRequestNotes;
            draft.currentFacilityRequestTab = this.initialState.currentFacilityRequestTab;
        }));
    }

    buildSmartActions(listItem: IFacilityRequestListItem) {
        return this.smartActionService.forDefaultStage(this, listItem);
    }

    buildSmartActionsForNotes(listItem: IFacilityRequestListItem) {
        return this.smartActionService.forNotes(this, listItem);
    }

    buildSmartActionsForInstance(listItem: IFacilityRequestListItem) {
        return this.smartActionService.forInstance(this, listItem);
    }

    @DataAction()
    async getFacilityRequestNotes(base64: string, facilityRequestExternalId: string) {

        const notes = await this.endpoint.getFacilityRequestNotes(base64, facilityRequestExternalId).toPromise();

        this.ctx.setState(produce(this.ctx.getState(), (draft: FacilityRequestStateModel) => {
            draft.currentFacilityRequestNotes = notes;
        }));

        return notes;
    }

    @DataAction()
    async addFacilityRequestNote(base64: string, facilityRequestExternalId: string, note: IFacilityNote) {

        await this.endpoint.addFacilityRequestNote(base64, facilityRequestExternalId, note).toPromise();
        const notes = await this.endpoint.getFacilityRequestNotes(base64, facilityRequestExternalId).toPromise();

        this.ctx.setState(produce(this.ctx.getState(), (draft: FacilityRequestStateModel) => {
            draft.currentFacilityRequestNotes = notes;
        }));
    }

    @DataAction()
    setCurrentFacilityRequestTab(@Payload('viewId') payload: string) {
        this.ctx.setState(produce(this.ctx.getState(), (draft: FacilityRequestStateModel) => {
            draft.currentFacilityRequestTab = payload;
        }));
    }

    @DataAction()
    setCurrentFacilityRequestItem(@Payload('currentFacilityRequest') payload: IFacilityRequestListItem) {
        this.ctx.setState(produce(this.ctx.getState(), (draft: FacilityRequestStateModel) => {
            draft.currentFacilityRequestListItem = payload;
        }));
    }

    @DataAction()
    setCurrentFacilityRequestForSmartAction(@Payload('currentFacilityRequestForAction') payload: IFacilityRequestForSmartAction) {
        this.ctx.setState(produce(this.ctx.getState(), (draft: FacilityRequestStateModel) => {
            draft.currentFacilityRequestForSmartAction = payload;
        }));
    }


    @DataAction()
    setCurrentFacilityRequestConfig(@Payload('currentFacilityRequestConfig') payload: IFacilityRequestConfig) {
        this.ctx.setState(produce(this.ctx.getState(), (draft: FacilityRequestStateModel) => {
            draft.currentFacilityRequestConfig = payload;
        }));
    }

}
